import { Button, ButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SWITCHER_RADIUS } from './fieldStyles';

const LANGUAGES = [
  { code: 'en', labelKey: 'layout.languageOption.english' },
  { code: 'fr', labelKey: 'layout.languageOption.french' },
] as const;

interface Props {
  /**
   * Called only when someone actually picks a language here, so the choice can be
   * saved to their profile on sign-in. The ambient detected locale is deliberately
   * not reported — otherwise a French browser would rewrite an English profile.
   */
  onChange?: (code: string) => void;
}

export const LanguageSwitcher = ({ onChange }: Props) => {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.split('-')[0] ?? 'en';

  return (
    <ButtonGroup
      size="small"
      variant="outlined"
      aria-label="language selector"
      // CNA-1969: a ButtonGroup does not pass its radius down, so the two end buttons need it
      // explicitly or they stay square inside a rounded box. Done this way rather than with
      // overflow:hidden so focus rings are not clipped. The value is shared with the region
      // switcher — see SWITCHER_RADIUS.
      sx={{
        borderRadius: SWITCHER_RADIUS,
        '& .MuiButtonGroup-grouped:first-of-type': {
          borderTopLeftRadius: SWITCHER_RADIUS,
          borderBottomLeftRadius: SWITCHER_RADIUS,
        },
        '& .MuiButtonGroup-grouped:last-of-type': {
          borderTopRightRadius: SWITCHER_RADIUS,
          borderBottomRightRadius: SWITCHER_RADIUS,
        },
      }}
    >
      {LANGUAGES.map(({ code, labelKey }) => (
        <Button
          key={code}
          onClick={() => {
            // Resolves once the locale bundle has loaded. Nothing here depends on that, but
            // the rejection must still be handled: a bundle that fails to load would otherwise
            // raise an unhandled rejection, and the page should simply stay in its current
            // language rather than break. The profile write is independent and still proceeds.
            i18n.changeLanguage(code).catch(() => undefined);
            onChange?.(code);
          }}
          // The visible label is the bare code, so on its own a screen reader announces
          // "EN". labelKey was already carried here for exactly this and left unused.
          aria-label={t(labelKey)}
          variant={current === code ? 'contained' : 'outlined'}
          disableElevation
          sx={{
            minWidth: 48,
            // CNA-1969: the switchers are wayfinding, not the point of the screen — keep them
            // a step below the form text so the card reads first.
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            fontWeight: current === code ? 700 : 400,
          }}
        >
          {code.toUpperCase()}
        </Button>
      ))}
    </ButtonGroup>
  );
};
